import { ref } from "vue";
import type { Attachment } from "@/dtos/Unipile.dto";
import { useAttachmentLoader } from "./useAttachmentLoader";

export const useAttachmentDownload = (attachment: Attachment, messageId?: string) => {
  const isDownloading = ref(false);
  const loadingProgress = ref(0);
  const { loadAttachment } = useAttachmentLoader(attachment, messageId, (progress) => {
    loadingProgress.value = progress;
  });

  const download = async (defaultMimeType: string, defaultFileName?: string) => {
    if (isDownloading.value) return;

    const url = attachment.url;
    if (!url) return;

    if (url.startsWith("att://") && messageId && attachment.id) {
      try {
        isDownloading.value = true;
        loadingProgress.value = 0;

        const blob = await loadAttachment(defaultMimeType);

        if (blob) {
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download =
            attachment.file_name ||
            attachment.name ||
            defaultFileName ||
            `attachment_${attachment.id}`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        }
      } catch (error) {
        console.error("Failed to download attachment:", error);
      } finally {
        isDownloading.value = false;
        setTimeout(() => {
          loadingProgress.value = 0;
        }, 300);
      }
    } else if (url) {
      window.open(url, "_blank");
    }
  };

  return {
    isDownloading,
    isLoading: ref(false),
    loadingProgress,
    download,
  };
};
